'use client'

import { useSelector, useDispatch } from 'react-redux'
import { useCallback } from 'react'
import { AppDispatch } from '@/store'
import {
  selectNotifications,
  selectUnreadCount,
  selectActiveTab,
  selectNotificationLoading,
  selectNotificationError,
  selectAllConfirmed,
  selectNoticesData,
  selectInfoData,
  markAsRead,
  markAllAsRead,
  setActiveTab,
  setError,
  getNotifications,
} from '@/store/notification/slice'
import { useAuth } from '@/hooks/useAuth'

export const useNotifications = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { getMemberId } = useAuth()
  const notifications = useSelector(selectNotifications)
  const unreadCount = useSelector(selectUnreadCount)
  const activeTab = useSelector(selectActiveTab)
  const isLoading = useSelector(selectNotificationLoading)
  const error = useSelector(selectNotificationError)
  const allConfirmed = useSelector(selectAllConfirmed)
  const noticesData = useSelector(selectNoticesData)
  const infoData = useSelector(selectInfoData)

  // タイプ別の通知を取得
  const getNotificationsByType = useCallback(
    (type: 'notices' | 'info') => {
      return notifications.filter((notification) => notification.type === type)
    },
    [notifications],
  )

  // タイプ別の未読数を取得
  const getUnreadCountByType = useCallback(
    (type: 'notices' | 'info') => {
      return notifications.filter(
        (notification) => notification.type === type && !notification.isRead,
      ).length
    },
    [notifications],
  )

  // APIから通知を取得
  const fetchNotifications = useCallback(async () => {
    const memberId = getMemberId()
    if (!memberId) {
      dispatch(setError('メンバーIDが見つかりません'))
      return
    }

    try {
      await dispatch(getNotifications(memberId))
    } catch (err) {
      dispatch(setError(err instanceof Error ? err.message : '通知の取得に失敗しました'))
    }
  }, [dispatch, getMemberId])

  // 通知を既読にする
  const markNotificationAsRead = useCallback(
    async (notificationId: number) => {
      try {
        dispatch(markAsRead(notificationId))
      } catch (err) {
        dispatch(setError(err instanceof Error ? err.message : '既読マークに失敗しました'))
      }
    },
    [dispatch],
  )

  // 指定タイプのすべての通知を既読にする
  const markAllNotificationsAsRead = useCallback(
    async (type: 'notices' | 'info') => {
      try {
        dispatch(markAllAsRead(type))
      } catch (err) {
        dispatch(setError(err instanceof Error ? err.message : '全て既読マークに失敗しました'))
      }
    },
    [dispatch],
  )

  // アクティブタブを変更
  const changeActiveTab = useCallback(
    (tab: 'notices' | 'info') => {
      dispatch(setActiveTab(tab))
    },
    [dispatch],
  )

  return {
    notifications,
    unreadCount,
    activeTab,
    isLoading,
    error,
    allConfirmed,
    noticesData,
    infoData,
    getNotificationsByType,
    getUnreadCountByType,
    fetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    changeActiveTab,
  }
}
